import {
  type InfiniteData,
  infiniteQueryOptions,
  type QueryClient,
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import type {
  AnalyticsWindow,
  AnalyzeJobDto,
  CreateTrackedWalletInput,
  CursorPage,
  ImportTrackedWalletsInput,
  ImportTrackedWalletsResult,
  LeaderSwapDto,
  TrackedWalletAnalyticsDetailDto,
  TrackedWalletAnalyticsDto,
  TrackedWalletDto,
  UpdateTrackedWalletInput,
} from '@ultrasnip/contracts';
import { api } from '@/lib/api-client';

export const TrackedKeys = {
  /** Persisted to IndexedDB (instant warm start). */
  list: ['tracked-wallets'] as const,
  feedAll: ['tracked-feed', 'all'] as const,
  feed: (id: string) => ['tracked-feed', id] as const,
  /** Not persisted: figures are cheap to refetch and go stale within a minute. */
  analytics: (window: AnalyticsWindow) => ['tracked-analytics', 'list', window] as const,
  walletAnalytics: (id: string, window: AnalyticsWindow) => ['tracked-analytics', id, window] as const,
  analysis: (jobId: string) => ['tracked-analysis', jobId] as const,
};

const ANALYTICS_ROOT = ['tracked-analytics'] as const;

/** The API caches figures for a minute; refreshing faster only repeats them. */
const ANALYTICS_REFRESH_MS = 60_000;

/** Every tracked wallet's figures for the window (the list's columns). */
export function analyticsQuery(window: AnalyticsWindow) {
  return queryOptions({
    queryKey: TrackedKeys.analytics(window),
    queryFn: ({ signal }) =>
      api.get<TrackedWalletAnalyticsDto[]>('/tracked-wallets/analytics', { query: { window }, signal }),
    staleTime: ANALYTICS_REFRESH_MS,
    refetchInterval: ANALYTICS_REFRESH_MS,
  });
}

/** One wallet's figures and daily cumulative PnL (its sheet). */
export function walletAnalyticsQuery(id: string, window: AnalyticsWindow) {
  return queryOptions({
    queryKey: TrackedKeys.walletAnalytics(id, window),
    queryFn: ({ signal }) =>
      api.get<TrackedWalletAnalyticsDetailDto>(`/tracked-wallets/${id}/analytics`, {
        query: { window },
        signal,
      }),
    staleTime: ANALYTICS_REFRESH_MS,
    refetchInterval: ANALYTICS_REFRESH_MS,
  });
}

/** Poll an analysis while the engine reads the address's history. */
export function analysisQuery(jobId: string | null) {
  return queryOptions({
    queryKey: TrackedKeys.analysis(jobId ?? ''),
    queryFn: ({ signal }) => api.get<AnalyzeJobDto>(`/tracked-wallets/analyze/${jobId}`, { signal }),
    enabled: jobId !== null,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'done' || status === 'failed' || query.state.error ? false : 1_500;
    },
    staleTime: Number.POSITIVE_INFINITY,
  });
}

/** Starts reading an address's recent history (or joins the read already running for it). */
export function startAnalysis(address: string): Promise<AnalyzeJobDto> {
  return api.post<AnalyzeJobDto>('/tracked-wallets/analyze', { address });
}

const FEED_PAGE = 30;

export const trackedWalletsQuery = queryOptions({
  queryKey: TrackedKeys.list,
  queryFn: ({ signal }) => api.get<TrackedWalletDto[]>('/tracked-wallets', { signal }),
  staleTime: 30_000,
});

/** Trades of one tracked wallet, or of all of them (`id` null). Live rows arrive over the socket. */
export function feedQuery(id: string | null) {
  return infiniteQueryOptions({
    queryKey: id ? TrackedKeys.feed(id) : TrackedKeys.feedAll,
    queryFn: ({ signal, pageParam }) =>
      api.get<CursorPage<LeaderSwapDto>>(id ? `/tracked-wallets/${id}/feed` : '/tracked-wallets/feed', {
        query: { limit: String(FEED_PAGE), ...(pageParam ? { cursor: pageParam } : {}) },
        signal,
      }),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    // The socket keeps it current; refetch only on explicit invalidation.
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function useTrackedMutations() {
  const queryClient = useQueryClient();
  const refresh = () => queryClient.invalidateQueries({ queryKey: TrackedKeys.list });
  const replaceOne = (updated: TrackedWalletDto) =>
    queryClient.setQueryData<TrackedWalletDto[]>(TrackedKeys.list, (list) =>
      list?.map((w) => (w.id === updated.id ? updated : w)),
    );

  const create = useMutation({
    mutationFn: (input: CreateTrackedWalletInput) => api.post<TrackedWalletDto>('/tracked-wallets', input),
    onSuccess: (created) => {
      queryClient.setQueryData<TrackedWalletDto[]>(TrackedKeys.list, (list) => [
        ...(list ?? []).filter((w) => w.id !== created.id),
        created,
      ]);
      void queryClient.invalidateQueries({ queryKey: ANALYTICS_ROOT });
    },
  });
  const update = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateTrackedWalletInput }) =>
      api.patch<TrackedWalletDto>(`/tracked-wallets/${id}`, input),
    onSuccess: replaceOne,
  });
  /** Copy on/off flips instantly; rolled back if the server refuses. */
  const setCopy = useMutation({
    mutationFn: ({ id, copyEnabled }: { id: string; copyEnabled: boolean }) =>
      api.patch<TrackedWalletDto>(`/tracked-wallets/${id}`, { copyEnabled }),
    onMutate: async ({ id, copyEnabled }) => {
      await queryClient.cancelQueries({ queryKey: TrackedKeys.list });
      const previous = queryClient.getQueryData<TrackedWalletDto[]>(TrackedKeys.list);
      queryClient.setQueryData<TrackedWalletDto[]>(TrackedKeys.list, (list) =>
        list?.map((w) => (w.id === id ? { ...w, copyEnabled } : w)),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => queryClient.setQueryData(TrackedKeys.list, context?.previous),
    onSuccess: replaceOne,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/tracked-wallets/${id}`),
    onSuccess: (_result, id) => {
      queryClient.setQueryData<TrackedWalletDto[]>(TrackedKeys.list, (list) =>
        list?.filter((w) => w.id !== id),
      );
      queryClient.removeQueries({ queryKey: TrackedKeys.feed(id) });
      queryClient.removeQueries({ queryKey: ['tracked-analytics', id] });
      void queryClient.invalidateQueries({ queryKey: TrackedKeys.feedAll });
    },
  });
  const importMany = useMutation({
    mutationFn: (input: ImportTrackedWalletsInput) =>
      api.post<ImportTrackedWalletsResult>('/tracked-wallets/import', input),
    onSuccess: refresh,
  });
  return { create, update, setCopy, remove, importMany };
}

type FeedData = InfiniteData<CursorPage<LeaderSwapDto>, string | null>;

function prepend(data: FeedData | undefined, swap: LeaderSwapDto): FeedData | undefined {
  if (!data) return data; // not loaded yet: the first fetch will include it
  const [first, ...rest] = data.pages;
  if (!first || data.pages.some((p) => p.items.some((i) => i.signature === swap.signature))) return data;
  return { ...data, pages: [{ ...first, items: [swap, ...first.items] }, ...rest] };
}

/** Applies a live leader swap to every cache that shows it (no refetch). */
export function applyLeaderSwap(queryClient: QueryClient, swap: LeaderSwapDto): void {
  queryClient.setQueryData<FeedData>(TrackedKeys.feedAll, (data) => prepend(data, swap));
  queryClient.setQueryData<FeedData>(TrackedKeys.feed(swap.trackedWalletId), (data) => prepend(data, swap));
  queryClient.setQueryData<TrackedWalletDto[]>(TrackedKeys.list, (list) =>
    list?.map((w) =>
      w.id === swap.trackedWalletId
        ? {
            ...w,
            stats: {
              ...w.stats,
              buysSeen: w.stats.buysSeen + (swap.side === 'buy' ? 1 : 0),
              sellsSeen: w.stats.sellsSeen + (swap.side === 'sell' ? 1 : 0),
              lastSwapAt: swap.observedAt,
            },
          }
        : w,
    ),
  );
}
